import React, { useState, useEffect } from "react";
import { Star, X, Clock, User } from "lucide-react";

// Star Rating Component
const StarRating = ({ rating, onRatingChange, disabled = false }) => {
  const [hoverRating, setHoverRating] = useState(0);

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={disabled}
          className={`p-1 transition-colors ${
            disabled ? "cursor-not-allowed" : "cursor-pointer hover:scale-110"
          }`}
          onMouseEnter={() => !disabled && setHoverRating(star)}
          onMouseLeave={() => !disabled && setHoverRating(0)}
          onClick={() => !disabled && onRatingChange(star)}
        >
          <Star
            className={`w-8 h-8 ${
              star <= (hoverRating || rating)
                ? "fill-yellow-400 text-yellow-400"
                : "text-gray-300"
            } transition-colors`}
          />
        </button>
      ))}
    </div>
  );
};

// Main Rating Modal Component
const RatingModal = ({
  isOpen,
  onClose,
  exchange,
  onSubmitRating,
  onRemindLater,
}) => {
  const [selectedRating, setSelectedRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedRating(0);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen || !exchange) return null;

  const handleSubmit = async () => {
    if (selectedRating === 0) return;

    setIsSubmitting(true);
    try {
      await onSubmitRating(exchange._id, selectedRating);
      onClose();
    } catch (error) {
      console.error("Failed to submit rating:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemindLater = async () => {
    setIsSubmitting(true);
    try {
      await onRemindLater(exchange._id);
      onClose();
    } catch (error) {
      console.error("Failed to set reminder:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const { userToRate } = exchange;
  const completedDate = new Date(exchange.completedAt).toLocaleDateString();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-auto animate-in fade-in-0 zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h3 className="text-xl font-semibold text-gray-800">
            Rate Your Exchange
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            disabled={isSubmitting}
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Exchange Info */}
          <div className="text-center mb-6">
            <div className="text-sm text-gray-500 mb-2">
              Exchange completed on {completedDate}
            </div>
            <div className="text-lg font-medium text-gray-800 mb-4">
              How was your experience with{" "}
              <span className="text-blue-600 font-semibold">
                {userToRate.firstName} {userToRate.lastName}
              </span>
              ?
            </div>
          </div>

          {/* User Profile */}
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              {userToRate.image ? (
                <img
                  src={userToRate.image}
                  alt={`${userToRate.firstName} ${userToRate.lastName}`}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-white" />
                </div>
              )}
              <div>
                <div className="font-medium text-gray-800">
                  {userToRate.firstName} {userToRate.lastName}
                </div>
                <div className="text-sm text-gray-500">Exchange Partner</div>
              </div>
            </div>
          </div>

          {/* Star Rating */}
          <div className="mb-6">
            <div className="mb-3 text-sm font-medium text-gray-700 text-center">
              Rate this exchange partner:
            </div>
            <div className="flex justify-center">
              <StarRating
                rating={selectedRating}
                onRatingChange={setSelectedRating}
                disabled={isSubmitting}
              />
            </div>
            {selectedRating > 0 && (
              <div className="mt-2 text-sm text-gray-600 text-center">
                {selectedRating === 1 && "Poor experience"}
                {selectedRating === 2 && "Below average"}
                {selectedRating === 3 && "Average experience"}
                {selectedRating === 4 && "Good experience"}
                {selectedRating === 5 && "Excellent experience"}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3">
            <button
              onClick={handleSubmit}
              disabled={selectedRating === 0 || isSubmitting}
              className={`w-full py-3 px-4 rounded-xl font-medium transition-colors ${
                selectedRating > 0 && !isSubmitting
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              {isSubmitting ? "Submitting..." : "Submit Rating"}
            </button>

            <button
              onClick={handleRemindLater}
              disabled={isSubmitting}
              className="w-full py-3 px-4 rounded-xl font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
            >
              <Clock className="w-4 h-4" />
              {isSubmitting ? "Setting reminder..." : "Remind me later (24h)"}
            </button>
          </div>

          {/* Helper Text */}
          <div className="mt-4 text-xs text-gray-500 text-center">
            Your rating helps other users make informed decisions about
            exchanges.
          </div>
        </div>
      </div>
    </div>
  );
};

export default RatingModal;
